import {
  ArrowDownIcon,
  ArrowsClockwiseIcon,
  ArrowUpIcon,
  BellIcon,
  BellSlashIcon,
  CheckCircleIcon,
  EyeIcon,
  WarningCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  inventoryWebSocket,
  type InventoryUpdate,
  type StockMovement,
} from '@/services/inventory-websocket.service';
import { formatCurrency, formatDate } from '@/utils';

interface RealTimeAlertsProps {
  autoConnect?: boolean;
  showMovements?: boolean;
  className?: string;
}

const RealTimeAlerts: React.FC<RealTimeAlertsProps> = ({
  autoConnect = true,
  showMovements = true,
  className = '',
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [connectionStatus, setConnectionStatus] =
    useState<string>('disconnected');

  // Real-time data
  const [alerts, setAlerts] = useState<InventoryUpdate[]>([]);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [metrics, setMetrics] = useState<any>(null);

  // UI state
  const [activeTab, setActiveTab] = useState<
    'alerts' | 'movements' | 'metrics'
  >('alerts');
  const [acknowledgedAlerts, setAcknowledgedAlerts] = useState<Set<string>>(
    new Set()
  );
  const [alertSounds, setAlertSounds] = useState(true);

  const { toast } = useToast();

  const showToast = (
    message: string,
    isSuccess?: boolean,
    variant?: string
  ) => {
    toast({
      title: isSuccess === false ? 'Error' : 'Success',
      description: message,
      variant: variant === 'destructive' ? 'destructive' : 'default',
    });
  };

  // ============================================================================
  // WebSocket Management
  // ============================================================================

  const connectWebSocket = useCallback(() => {
    if (!isConnected) {
      inventoryWebSocket.connect();
    }
  }, [isConnected]);

  const disconnectWebSocket = useCallback(() => {
    if (isConnected) {
      inventoryWebSocket.stopInventoryMonitoring();
      inventoryWebSocket.disconnect();
    }
  }, [isConnected]);

  const toggleMonitoring = useCallback(() => {
    if (isMonitoring) {
      inventoryWebSocket.stopInventoryMonitoring();
      setIsMonitoring(false);
      showToast('Real-time monitoring stopped', true);
    } else {
      inventoryWebSocket.startInventoryMonitoring();
      setIsMonitoring(true);
      showToast('Real-time monitoring started', true);
    }
  }, [isMonitoring, showToast]);

  // ============================================================================
  // Effect Hooks
  // ============================================================================

  useEffect(() => {
    if (autoConnect) {
      connectWebSocket();
    }

    // Setup WebSocket event listeners
    const unsubscribeStatus = inventoryWebSocket.onStatusChange(status => {
      setConnectionStatus(status);
      setIsConnected(status === 'connected');

      if (status === 'connected') {
        showToast('Real-time connection established', true);
        if (autoConnect) {
          inventoryWebSocket.startInventoryMonitoring();
          setIsMonitoring(true);
        }
      } else if (status === 'disconnected') {
        setIsMonitoring(false);
        showToast('Real-time connection lost', false);
      } else if (status === 'error') {
        showToast('Connection error - will retry automatically', false);
      }
    });

    const unsubscribeUpdates = inventoryWebSocket.subscribeToInventoryUpdates(
      update => {
        setAlerts(prev => {
          const newAlerts = [update, ...prev.slice(0, 99)]; // Keep last 100 alerts

          // Play sound for critical alerts
          if (
            alertSounds &&
            (update.severity === 'high' || update.severity === 'critical')
          ) {
            playAlertSound();
          }

          // Show toast notification
          const severityColors = {
            low: 'default',
            medium: 'warning',
            high: 'destructive',
            critical: 'destructive',
          };

          showToast(
            `${update.itemName}: ${update.message}`,
            update.severity === 'low',
            severityColors[update.severity]
          );

          return newAlerts;
        });
      }
    );

    const unsubscribeMovements = inventoryWebSocket.subscribeToStockMovements(
      movement => {
        if (showMovements) {
          setMovements(prev => [movement, ...prev.slice(0, 49)]); // Keep last 50 movements
        }
      }
    );

    const unsubscribeMetrics = inventoryWebSocket.subscribeToRealtimeMetrics(
      newMetrics => {
        setMetrics(newMetrics);
      }
    );

    return () => {
      unsubscribeStatus();
      unsubscribeUpdates();
      unsubscribeMovements();
      unsubscribeMetrics();

      if (autoConnect) {
        disconnectWebSocket();
      }
    };
  }, [
    autoConnect,
    showMovements,
    alertSounds,
    connectWebSocket,
    disconnectWebSocket,
    showToast,
  ]);

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const playAlertSound = () => {
    // Create a simple beep sound
    try {
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();

      oscillator.connect(gain);
      gain.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      gain.gain.setValueAtTime(0.1, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.5
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {}
  };

  const acknowledgeAlert = (alertId: string) => {
    setAcknowledgedAlerts(prev => new Set(prev).add(alertId));
    inventoryWebSocket.acknowledgeAlert(alertId, 'current-user-id'); // Replace with actual user ID
    showToast('Alert acknowledged');
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low':
        return 'text-blue-60 bg-blue-5 border-blue-20';
      case 'medium':
        return 'text-warning-60 bg-warning-5 border-warning-20';
      case 'high':
        return 'text-destructive-60 bg-destructive-5 border-destructive-20';
      case 'critical':
        return 'text-red-60 bg-red-5 border-red-20 animate-pulse';
      default:
        return 'text-gray-60 bg-gray-5 border-gray-20';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'low':
        return <CheckCircleIcon className='h-4 w-4 text-blue-60' />;
      case 'medium':
        return <WarningCircleIcon className='h-4 w-4 text-warning-60' />;
      case 'high':
        return <WarningCircleIcon className='h-4 w-4 text-destructive-60' />;
      case 'critical':
        return <XCircleIcon className='h-4 w-4 text-red-60' />;
      default:
        return <BellIcon className='h-4 w-4 text-gray-60' />;
    }
  };

  const getMovementIcon = (type: string) => {
    switch (type) {
      case 'in':
        return <ArrowUpIcon className='h-4 w-4 text-success-60' />;
      case 'out':
        return <ArrowDownIcon className='h-4 w-4 text-destructive-60' />;
      case 'reserved':
        return <ArrowsClockwiseIcon className='h-4 w-4 text-warning-60' />;
      case 'released':
        return <CheckCircleIcon className='h-4 w-4 text-blue-60' />;
      default:
        return <ArrowsClockwiseIcon className='h-4 w-4 text-gray-60' />;
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'text-success-60';
      case 'connecting':
      case 'reconnecting':
        return 'text-warning-60';
      case 'disconnected':
      case 'error':
        return 'text-destructive-60';
      default:
        return 'text-gray-60';
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Card className={className}>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <BellIcon className='h-5 w-5 text-brand-60' />
            <div>
              <CardTitle>Real-Time Inventory Alerts</CardTitle>
              <CardDescription>
                Live monitoring of stock levels and inventory changes
              </CardDescription>
            </div>
          </div>

          <div className='flex items-center gap-3'>
            {/* Connection Status */}
            <div className='flex items-center gap-2'>
              <div
                className={`h-2 w-2 rounded-full ${getConnectionStatusColor().replace('text-', 'bg-')}`}
              />
              <span className={`text-xs ${getConnectionStatusColor()}`}>
                {connectionStatus}
              </span>
            </div>

            {/* Alert Sound Toggle */}
            <div className='flex items-center gap-2'>
              {alertSounds ? (
                <BellIcon className='h-4 w-4 text-gray-60' />
              ) : (
                <BellSlashIcon className='h-4 w-4 text-gray-60' />
              )}
              <Switch checked={alertSounds} onCheckedChange={setAlertSounds} />
            </div>

            {/* Monitoring Toggle */}
            <Button
              hierarchy={isMonitoring ? 'primary' : 'outlined'}
              size='sm'
              onClick={toggleMonitoring}
              disabled={!isConnected}
            >
              {isMonitoring ? 'Stop' : 'Start'} Monitoring
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Real-time Metrics */}
        {metrics && (
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
            <Card className='p-3'>
              <div className='text-center'>
                <div className='text-lg font-semibold'>
                  {metrics.totalItems}
                </div>
                <div className='text-xs text-gray-60'>Total Items</div>
              </div>
            </Card>
            <Card className='p-3'>
              <div className='text-center'>
                <div className='text-lg font-semibold text-destructive-60'>
                  {metrics.lowStockItems}
                </div>
                <div className='text-xs text-gray-60'>Low Stock</div>
              </div>
            </Card>
            <Card className='p-3'>
              <div className='text-center'>
                <div className='text-lg font-semibold text-red-60'>
                  {metrics.outOfStockItems}
                </div>
                <div className='text-xs text-gray-60'>Out of Stock</div>
              </div>
            </Card>
            <Card className='p-3'>
              <div className='text-center'>
                <div className='text-lg font-semibold text-success-60'>
                  {formatCurrency(metrics.totalValue)}
                </div>
                <div className='text-xs text-gray-60'>Total Value</div>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={value =>
            setActiveTab(value as 'alerts' | 'movements' | 'metrics')
          }
        >
          <TabsList className='grid w-full grid-cols-3'>
            <TabsTrigger value='alerts'>Alerts ({alerts.length})</TabsTrigger>
            {showMovements && (
              <TabsTrigger value='movements'>
                Movements ({movements.length})
              </TabsTrigger>
            )}
            <TabsTrigger value='metrics'>Metrics</TabsTrigger>
          </TabsList>

          {/* Alerts Tab */}
          <TabsContent value='alerts' className='mt-4'>
            {!isConnected ? (
              <Alert>
                <AlertDescription>
                  Connect to real-time monitoring to see live alerts
                </AlertDescription>
              </Alert>
            ) : alerts.length === 0 ? (
              <div className='text-center py-8 text-gray-60'>
                <BellIcon className='h-12 w-12 mx-auto mb-4 text-gray-40' />
                <p>No alerts yet</p>
                <p className='text-sm mt-1'>
                  Stock alerts will appear here in real-time
                </p>
              </div>
            ) : (
              <div className='space-y-2 max-h-96 overflow-y-auto'>
                {alerts.map(alert => (
                  <Card
                    key={alert.id}
                    className={`p-4 ${acknowledgedAlerts.has(alert.id) ? 'opacity-60' : ''}`}
                  >
                    <div className='flex items-start justify-between'>
                      <div className='flex items-start gap-3'>
                        {getSeverityIcon(alert.severity)}
                        <div className='flex-1'>
                          <div className='flex items-center gap-2 mb-1'>
                            <span className='font-medium'>
                              {alert.itemName}
                            </span>
                            <Badge className={getSeverityColor(alert.severity)}>
                              {alert.severity.toUpperCase()}
                            </Badge>
                          </div>
                          <p className='text-sm text-gray-70 mb-1'>
                            {alert.message}
                          </p>
                          <div className='flex items-center gap-4 text-xs text-gray-60'>
                            <span>SKU: {alert.sku}</span>
                            <span>Stock: {alert.currentStock}</span>
                            <span>{formatDate(alert.timestamp)}</span>
                          </div>
                        </div>
                      </div>

                      {!acknowledgedAlerts.has(alert.id) && (
                        <Button
                          hierarchy='outlined'
                          size='sm'
                          onClick={() => acknowledgeAlert(alert.id)}
                        >
                          <EyeIcon className='h-4 w-4' />
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Movements Tab */}
          {showMovements && (
            <TabsContent value='movements' className='mt-4'>
              {movements.length === 0 ? (
                <div className='text-center py-8 text-gray-60'>
                  <ArrowsClockwiseIcon className='h-12 w-12 mx-auto mb-4 text-gray-40' />
                  <p>No stock movements yet</p>
                  <p className='text-sm mt-1'>
                    Real-time inventory movements will appear here
                  </p>
                </div>
              ) : (
                <div className='space-y-2 max-h-96 overflow-y-auto'>
                  {movements.map(movement => (
                    <Card key={movement.id} className='p-4'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-3'>
                          {getMovementIcon(movement.movementType)}
                          <div>
                            <div className='font-medium'>
                              {movement.itemName}
                            </div>
                            <div className='text-sm text-gray-60'>
                              {movement.sku}
                            </div>
                          </div>
                        </div>

                        <div className='text-right'>
                          <div className='font-medium'>
                            {movement.movementType === 'in' ? '+' : '-'}
                            {movement.quantity}
                          </div>
                          <div className='text-sm text-gray-60'>
                            {movement.previousStock} → {movement.newStock}
                          </div>
                          <div className='text-xs text-gray-50'>
                            {formatDate(movement.timestamp)}
                          </div>
                        </div>
                      </div>
                      {movement.reason && (
                        <p className='text-sm text-gray-60 mt-2'>
                          Reason: {movement.reason}
                        </p>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* Metrics Tab */}
          <TabsContent value='metrics' className='mt-4'>
            {metrics ? (
              <div className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <Card className='p-4'>
                    <h4 className='font-medium mb-3'>Inventory Health</h4>
                    <div className='space-y-3'>
                      <div>
                        <div className='flex justify-between text-sm mb-1'>
                          <span>Low Stock Items</span>
                          <span>{metrics.lowStockItems}</span>
                        </div>
                        <Progress
                          value={
                            (metrics.lowStockItems / metrics.totalItems) * 100
                          }
                          className='h-2'
                        />
                      </div>
                      <div>
                        <div className='flex justify-between text-sm mb-1'>
                          <span>Out of Stock</span>
                          <span>{metrics.outOfStockItems}</span>
                        </div>
                        <Progress
                          value={
                            (metrics.outOfStockItems / metrics.totalItems) * 100
                          }
                          className='h-2'
                          // variant="destructive"
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className='p-4'>
                    <h4 className='font-medium mb-3'>Performance</h4>
                    <div className='space-y-2'>
                      <div className='flex justify-between'>
                        <span className='text-sm text-gray-60'>
                          Avg Turnover:
                        </span>
                        <span className='font-medium'>
                          {metrics.averageTurnover.toFixed(1)}×
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span className='text-sm text-gray-60'>
                          Total Value:
                        </span>
                        <span className='font-medium'>
                          {formatCurrency(metrics.totalValue)}
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span className='text-sm text-gray-60'>
                          Last Updated:
                        </span>
                        <span className='text-sm'>
                          {formatDate(metrics.lastUpdated)}
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            ) : (
              <div className='text-center py-8 text-gray-60'>
                <Skeleton className='h-24 w-full mb-4' />
                <p>Loading metrics...</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default RealTimeAlerts;
